import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Modal, TextField, FormControlLabel, Switch, Dialog, DialogTitle, DialogActions, FormControl, InputLabel, Select, MenuItem, IconButton, Menu, ListItemIcon, ListItemText } from '@mui/material';
import { Cake as CakeIcon, WorkHistory as WorkHistoryIcon, Edit as EditIcon, AddCircle as AddCircleIcon } from '@mui/icons-material';
import EmailSignatureEditor from './EmailSignatureEditor.js';
import { useDomain } from '../../context/DomainContext.js'
import features from '../../config/features.js';

function TemplateModal({ openModal, selectedTemplate, templateTypeToCreate, handleCloseModal, handleSaveTemplate, keywords }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [templateHtml, setTemplateHtml] = useState('');
  const [templateType, setTemplateType] = useState('normal');
  const [language, setLanguage] = useState('es');
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [keywordMenuAnchor, setKeywordMenuAnchor] = useState(null);
  const { selectedDomain } = useDomain();

  useEffect(() => {
    if (selectedTemplate?.template_name) {
      // Editando template existente
      const data = {
        template_name: selectedTemplate.template_name,
        description: selectedTemplate.description,
        email_subject: selectedTemplate.email_subject || '',
        is_active: !!selectedTemplate.is_active,
        template_html: selectedTemplate.template_html,
        template_type: selectedTemplate.template_type || 'normal',
        language: selectedTemplate.language || 'es',
      };
      setName(data.template_name);
      setDescription(data.description);
      setEmailSubject(data.email_subject);
      setIsActive(data.is_active);
      setTemplateHtml(data.template_html);
      setTemplateType(data.template_type);
      setLanguage(data.language);
      setIsNewTemplate(false);
      setChangesMade(false);
    } else {
      // Creando nuevo template
      setName('');
      setDescription('');
      setEmailSubject('');
      setIsActive(true);
      setTemplateHtml('');
      setTemplateType(templateTypeToCreate || 'normal');
      setLanguage('es');
      setIsNewTemplate(true);
      setChangesMade(false);
    }
  }, [selectedTemplate, templateTypeToCreate]);

  const handleActiveChange = (event) => {
    setIsActive(event.target.checked);
    setChangesMade(true);
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    setChangesMade(true);
  };

  const handleEditorChange = (newHtml) => {
    if (selectedTemplate?.template_html !== newHtml) {
      setTemplateHtml(newHtml);
      setChangesMade(true);
    }
  };

  // Insertar keyword en el campo de asunto
  const handleInsertKeywordInSubject = (keyword) => {
    const newSubject = emailSubject + `{{${keyword}}}`;
    setEmailSubject(newSubject);
    setChangesMade(true);
    setKeywordMenuAnchor(null); // Cerrar el menú
  };

  const handleOpenKeywordMenu = (event) => {
    setKeywordMenuAnchor(event.currentTarget);
  };

  const handleCloseKeywordMenu = () => {
    setKeywordMenuAnchor(null);
  };

  const handleSave = () => {
    const templateData = {
      template_name: name,
      description,
      email_subject: emailSubject,
      is_active: isActive,
      template_html: templateHtml,
      template_type: templateType,
      language: templateType !== 'normal' ? language : null,
      domain_id: selectedDomain,
    };
    handleSaveTemplate(templateData, isNewTemplate);
    setChangesMade(false);
  };

  const handleCloseRequest = () => {
    if (changesMade) {
      setConfirmClose(true);
    } else {
      handleCloseModal();
    }
  };

  const confirmDiscardChanges = () => {
    setConfirmClose(false);
    handleCloseModal();
  };

  const getModalTitle = () => {
    if (!isNewTemplate) {
      return (
        <>
          Editar Template
          {selectedTemplate?.id && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', opacity: 0.7, fontSize: '0.65em' }}>
              (ID: {selectedTemplate.id})
            </Typography>
          )}
        </>
      );
    }
    
    switch (templateType) {
      case 'birthday':
        return 'Crear Plantilla de Cumpleaños';
      case 'anniversary':
        return 'Crear Plantilla de Aniversario';
      default:
        return 'Crear Template';
    }
  };

  const getSubjectPlaceholder = () => {
    if (templateType === 'birthday') {
      return language === 'es' 
        ? '¡Feliz Cumpleaños {{givenName}}!' 
        : 'Feliz Aniversário {{givenName}}!';
    }
    if (templateType === 'anniversary') {
      return language === 'es'
        ? '¡Feliz Aniversario Laboral {{givenName}}!'
        : 'Feliz Aniversário de Trabalho {{givenName}}!';
    }
    return 'Asunto del correo';
  };

  // Mostrar campo de asunto solo para templates especiales
  const shouldShowSubjectField = templateType !== 'normal';

  return (
    <>
      <Modal open={openModal} onClose={handleCloseRequest} data-testid="template-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80vw',
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {getModalTitle()}
          </Typography>
          
          {/* Selector de tipo de plantilla - Solo al crear y si las plantillas especiales están habilitadas */}
          {isNewTemplate && features.specialTemplatesEnabled && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="template-type-label">Tipo de Plantilla</InputLabel>
              <Select
                labelId="template-type-label"
                value={templateType}
                label="Tipo de Plantilla"
                onChange={(e) => {
                  setTemplateType(e.target.value);
                  setChangesMade(true);
                }}
              >
                <MenuItem value="normal">
                  <Box display="flex" alignItems="center" gap={1}>
                    <EditIcon fontSize="small" />
                    Firma Normal
                  </Box>
                </MenuItem>
                <MenuItem value="birthday">
                  <Box display="flex" alignItems="center" gap={1}>
                    <CakeIcon fontSize="small" />
                    Plantilla de Cumpleaños
                  </Box>
                </MenuItem>
                <MenuItem value="anniversary">
                  <Box display="flex" alignItems="center" gap={1}>
                    <WorkHistoryIcon fontSize="small" />
                    Plantilla de Aniversario
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Selector de idioma - Solo para templates especiales */}
          {(templateType === 'birthday' || templateType === 'anniversary') && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="language-label">Idioma</InputLabel>
              <Select
                labelId="language-label"
                value={language}
                label="Idioma"
                onChange={(e) => {
                  setLanguage(e.target.value);
                  setChangesMade(true);
                }}
                disabled={!isNewTemplate} // Solo editable al crear
              >
                <MenuItem value="es">
                  <Box display="flex" alignItems="center" gap={1}>
                    🇨🇱 Español (Chile)
                  </Box>
                </MenuItem>
                <MenuItem value="pt">
                  <Box display="flex" alignItems="center" gap={1}>
                    🇧🇷 Português (Brasil)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}
          
          <TextField
            fullWidth
            label="Nombre"
            variant="outlined"
            margin="normal"
            value={name}
            onChange={handleInputChange(setName)}
            helperText={
              templateType !== 'normal' 
                ? `Ejemplo: ${templateType === 'birthday' ? 'Birthday' : 'Anniversary'} ${language === 'es' ? 'CL' : 'BR'}`
                : ''
            }
          />

          {/* Campo de Asunto - Solo para templates especiales */}
          {shouldShowSubjectField && (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2 }}>
                <TextField
                  fullWidth
                  label="Asunto del correo"
                  variant="outlined"
                  value={emailSubject}
                  onChange={handleInputChange(setEmailSubject)}
                  placeholder={getSubjectPlaceholder()}
                  helperText="Haz clic en el ícono para insertar palabras clave"
                />
                
                {/* Botón para abrir menú de keywords */}
                <IconButton
                  color="warning"
                  onClick={handleOpenKeywordMenu}
                  sx={{
                    mt: 1,
                    bgcolor: 'warning.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'warning.dark',
                    },
                  }}
                  title="Insertar palabra clave"
                >
                  <AddCircleIcon />
                </IconButton>
              </Box>

              {/* Menú desplegable con keywords */}
              <Menu
                anchorEl={keywordMenuAnchor}
                open={Boolean(keywordMenuAnchor)}
                onClose={handleCloseKeywordMenu}
                PaperProps={{
                  style: {
                    maxHeight: 400,
                    width: '300px',
                  },
                }}
              >
                {keywords && keywords.length > 0 ? (
                  // Ordenar keywords: givenName, familyName, birthday, aniversary primero, luego el resto alfabéticamente
                  [...keywords]
                    .sort((a, b) => {
                      // Prioridad 1: givenName
                      if (a.name === 'givenName') return -1;
                      if (b.name === 'givenName') return 1;
                      // Prioridad 2: familyName
                      if (a.name === 'familyName') return -1;
                      if (b.name === 'familyName') return 1;
                      // Prioridad 3: birthday
                      if (a.name === 'birthday') return -1;
                      if (b.name === 'birthday') return 1;
                      // Prioridad 4: aniversary
                      if (a.name === 'aniversary') return -1;
                      if (b.name === 'aniversary') return 1;
                      // Resto: orden alfabético
                      return a.name.localeCompare(b.name);
                    })
                    .map((keyword) => (
                      <MenuItem
                        key={keyword.id}
                        onClick={() => handleInsertKeywordInSubject(keyword.name)}
                      >
                        <ListItemIcon>
                          <AddCircleIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={keyword.name}
                          secondary={`{{${keyword.name}}}`}
                        />
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled>
                    <ListItemText primary="No hay palabras clave disponibles" />
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          
          <TextField
            fullWidth
            label="Descripción"
            variant="outlined"
            margin="normal"
            value={description}
            onChange={handleInputChange(setDescription)}
          />
          
          <FormControlLabel
            style={{ width: '100%', marginBottom: '8px' }}
            control={<Switch checked={isActive} onChange={handleActiveChange} />}
            label="Template activo"
          />
          
          <EmailSignatureEditor 
            value={templateHtml} 
            onChange={handleEditorChange} 
            keywords={keywords} 
          />
          
          <Button 
            variant="contained" 
            onClick={handleSave} 
            sx={{ mt: 1, mr: 2 }} 
            disabled={!name || !description || !templateHtml}
          >
            Guardar Cambios
          </Button>
          <Button variant="outlined" onClick={handleCloseRequest} sx={{ mt: 1 }}>
            Cancelar
          </Button>
        </Box>
      </Modal>
      
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        fullWidth={true}
        maxWidth={'xs'}
        PaperProps={{
          sx: {
            padding: 3,
          },
        }}
      >
        <DialogTitle>¿Descartar cambios?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)} color="primary" data-testid="cancel-button-template-modal-confirm">
            Cancelar
          </Button>
          <Button onClick={confirmDiscardChanges} color="error">
            Sí, descartar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TemplateModal;